import { useState, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, CheckCircle } from "lucide-react";
import { useLang } from "@/LanguageContext";
import t from "@/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterSection() {
  const { lang } = useLang();
  const tx = t[lang];
  const [form, setForm] = useState({ salutation: "formal", title: "", first_name: "", last_name: "", email: "", topics: ["general"] });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const formTs = useRef(Math.floor(Date.now() / 1000));

  const toggleTopic = (topic) => setForm((p) => ({ ...p, topics: p.topics.includes(topic) ? p.topics.filter((x) => x !== topic) : [...p.topics, topic] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.topics.length === 0) { setError(tx.nl_error_topics); return; }
    setSending(true); setError("");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { ...form, _website: honeypot, _ts: formTs.current });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (lang === "de" ? "Anmeldung fehlgeschlagen." : "Subscription failed."));
    } finally { setSending(false); }
  };

  return (
    <section id="newsletter" data-testid="newsletter-section" className="py-16 sm:py-24 lg:py-32 bg-[#0C1620] relative">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3A4F60]/50 to-transparent" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-6 sm:p-10">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-lg bg-[#C5993A]/10 flex items-center justify-center mx-auto mb-4"><Mail className="w-6 h-6 text-[#C5993A]" /></div>
            <h2 className="font-heading text-2xl sm:text-3xl text-white font-light mb-2">{tx.nl_h2}</h2>
            <p className="text-[#8899AA] text-sm font-light">{tx.nl_sub}</p>
          </div>

          {sent ? (
            <div data-testid="newsletter-success" className="text-center py-6">
              <CheckCircle className="w-14 h-14 text-[#C5993A] mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white mb-2">{tx.nl_success_title}</h3>
              <p className="text-[#8899AA] text-sm">{tx.nl_success_text}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="newsletter-form">
              <div style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0, overflow: "hidden" }} aria-hidden="true">
                <input type="text" name="_website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} autoComplete="off" tabIndex={-1} />
              </div>

              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="salutation" value="formal" checked={form.salutation === "formal"} onChange={() => setForm({ ...form, salutation: "formal" })} className="accent-[#C5993A]" />
                  <span className="text-xs sm:text-sm text-[#F0F4F8]">{tx.nl_formal}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="salutation" value="informal" checked={form.salutation === "informal"} onChange={() => setForm({ ...form, salutation: "informal" })} className="accent-[#C5993A]" />
                  <span className="text-xs sm:text-sm text-[#F0F4F8]">{tx.nl_informal}</span>
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div><label className="block text-xs text-[#8899AA] mb-1">{tx.nl_title}</label><Input data-testid="nl-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="z.B. Dr." className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A]" /></div>
                <div><label className="block text-xs text-[#8899AA] mb-1">{tx.nl_firstname}</label><Input data-testid="nl-firstname-input" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A]" /></div>
                <div><label className="block text-xs text-[#8899AA] mb-1">{tx.nl_lastname}</label><Input data-testid="nl-lastname-input" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A]" /></div>
              </div>

              <div><label className="block text-xs text-[#8899AA] mb-1">{tx.nl_email}</label><Input data-testid="nl-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A]" /></div>

              <div>
                <label className="block text-xs text-[#8899AA] mb-2">{tx.nl_topics}</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-[#3A4F60]/30 hover:border-[#C5993A]/30 transition-colors">
                    <Checkbox data-testid="nl-topic-general" checked={form.topics.includes("general")} onCheckedChange={() => toggleTopic("general")} className="border-[#3A4F60] data-[state=checked]:bg-[#C5993A] data-[state=checked]:border-[#C5993A]" />
                    <div><span className="text-sm text-white">{tx.nl_topic_general}</span><p className="text-[10px] text-[#3A4F60]">{tx.nl_topic_general_desc}</p></div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-[#3A4F60]/30 hover:border-[#C5993A]/30 transition-colors">
                    <Checkbox data-testid="nl-topic-events" checked={form.topics.includes("events")} onCheckedChange={() => toggleTopic("events")} className="border-[#3A4F60] data-[state=checked]:bg-[#C5993A] data-[state=checked]:border-[#C5993A]" />
                    <div><span className="text-sm text-white">{tx.nl_topic_events}</span><p className="text-[10px] text-[#3A4F60]">{tx.nl_topic_events_desc}</p></div>
                  </label>
                </div>
              </div>

              <p className="text-[10px] text-[#3A4F60]">
                {tx.nl_privacy}{" "}<a href="/datenschutz" className="text-[#C5993A] hover:underline">{tx.nl_privacy_link}</a>{" "}{tx.nl_privacy_end}
              </p>
              {error && <p data-testid="nl-error" className="text-red-400 text-xs">{error}</p>}
              <Button data-testid="nl-submit-button" type="submit" disabled={sending} className="w-full bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white font-medium py-5 text-sm rounded hover:from-[#8B6B20] hover:to-[#C5993A] transition-all border-0">
                {sending ? "..." : tx.nl_submit}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
